import { formatDateTime } from '../../../shared/format/index.js';
import { ApiError } from '../../../shared/api-client/index.js';
import type { EntityStatusTone } from '../../../components/entity-detail/EntityStatusChip.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { useTranslation } from '@govard/i18n-core/react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { Button, Card, Tag, message, theme } from 'antd';
import { Check, CircleSlash2, Clock3, RotateCcw, X } from 'lucide-react';
import { useState, type ReactElement } from 'react';

export type CrmActionPlan = Extract<
  StructuredMessagePayload,
  { type: 'crm_action_plan_v1' }
>['plan'];
export type CrmActionPlanItem = CrmActionPlan['items'][number];

export interface CrmActionPlanDecisionRequest {
  planId: string;
  itemId: string;
  approvalId: string;
  decision: 'approve' | 'reject';
  remainingToken?: string;
}

export interface CrmActionPlanUndoRequest {
  planId: string;
  itemId: string;
}

export interface CrmActionPlanCallbacks {
  onDecideActionPlanItem?:
    | ((request: CrmActionPlanDecisionRequest) => void | Promise<void>)
    | undefined;
  onRequestActionPlanRetry?:
    | ((request: CrmActionPlanUndoRequest & { failedExecutionId: string }) => void | Promise<void>)
    | undefined;
  onRequestActionPlanUndo?:
    | ((request: CrmActionPlanUndoRequest) => void | Promise<void>)
    | undefined;
  onOpenEntity?: ((type: 'deal' | 'contact', id: string, title?: string) => void) | undefined;
}

interface CrmActionPlanCardProps extends CrmActionPlanCallbacks {
  plan: CrmActionPlan;
}

const STATUS_TONES: Readonly<Record<CrmActionPlanItem['status'], EntityStatusTone>> = {
  blocked: 'error',
  pending: 'warning',
  approved: 'processing',
  rejected: 'default',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'default',
  undo_pending: 'warning',
  undone: 'default',
  stale: 'error',
};

const ACTION_KEYS: Readonly<Record<string, string>> = {
  create_company: 'crm.ai_chat.action_plan.action.create_company',
  create_contact: 'crm.ai_chat.action_plan.action.create_contact',
  create_deal: 'crm.ai_chat.action_plan.action.create_deal',
  create_task: 'crm.ai_chat.action_plan.action.create_task',
  create_calendar_event: 'crm.ai_chat.action_plan.action.create_calendar_event',
  create_contact_interaction: 'crm.ai_chat.action_plan.action.create_contact_interaction',
  create_note: 'crm.ai_chat.action_plan.action.create_note',
  create_document: 'crm.ai_chat.action_plan.action.create_document',
  create_template: 'crm.ai_chat.action_plan.action.create_template',
  update_task: 'crm.ai_chat.action_plan.action.update_task',
  update_company: 'crm.ai_chat.action_plan.action.update_company',
  update_contact: 'crm.ai_chat.action_plan.action.update_contact',
  update_deal: 'crm.ai_chat.action_plan.action.update_deal',
  update_note: 'crm.ai_chat.action_plan.action.update_note',
  update_document: 'crm.ai_chat.action_plan.action.update_document',
  update_template: 'crm.ai_chat.action_plan.action.update_template',
  update_calendar_event: 'crm.ai_chat.action_plan.action.update_calendar_event',
  delete_company: 'crm.ai_chat.action_plan.action.delete_company',
  delete_contact: 'crm.ai_chat.action_plan.action.delete_contact',
  delete_deal: 'crm.ai_chat.action_plan.action.delete_deal',
  delete_task: 'crm.ai_chat.action_plan.action.delete_task',
  delete_note: 'crm.ai_chat.action_plan.action.delete_note',
  delete_document: 'crm.ai_chat.action_plan.action.delete_document',
  delete_template: 'crm.ai_chat.action_plan.action.delete_template',
  cancel_calendar_event: 'crm.ai_chat.action_plan.action.cancel_calendar_event',
  crm_bulk_group_apply: 'crm.ai_chat.action_plan.action.bulk_group_apply',
};

const FIELD_KEYS: Readonly<Record<string, string>> = {
  name: 'crm.ai_chat.action_plan.field.name',
  firstName: 'crm.ai_chat.action_plan.field.first_name',
  lastName: 'crm.ai_chat.action_plan.field.last_name',
  phone: 'crm.ai_chat.action_plan.field.phone',
  email: 'crm.ai_chat.action_plan.field.email',
  title: 'crm.ai_chat.action_plan.field.title',
  amount: 'crm.ai_chat.action_plan.field.amount',
  stage: 'crm.ai_chat.action_plan.field.stage',
  companyId: 'crm.ai_chat.action_plan.field.company',
  contactId: 'crm.ai_chat.action_plan.field.contact',
  dealId: 'crm.ai_chat.action_plan.field.deal',
  dueAt: 'crm.ai_chat.action_plan.field.due_at',
  description: 'crm.ai_chat.action_plan.field.description',
  priority: 'crm.ai_chat.action_plan.field.priority',
  assignee: 'crm.approvals.detail.assignee',
  startsAt: 'crm.ai_chat.action_plan.field.starts_at',
  endsAt: 'crm.ai_chat.action_plan.field.ends_at',
  timezone: 'crm.ai_chat.action_plan.field.timezone',
  location: 'crm.ai_chat.action_plan.field.location',
  type: 'crm.ai_chat.action_plan.field.type',
  subject: 'crm.ai_chat.action_plan.field.subject',
  body: 'crm.ai_chat.action_plan.field.body',
  channel: 'crm.ai_chat.action_plan.field.channel',
  occurredAt: 'crm.ai_chat.action_plan.field.occurred_at',
};

const DATE_FIELDS = new Set(['dueAt', 'startsAt', 'endsAt', 'occurredAt']);

function formatFieldValue(
  field: string,
  value: string | number | boolean | null,
  empty: string,
): string {
  if (value === null || value === '') return empty;
  if (typeof value === 'boolean') return value ? '✓' : '—';
  if (DATE_FIELDS.has(field) && typeof value === 'string') {
    const formatted = formatDateTime(value);
    if (formatted !== '—') return formatted;
  }
  return String(value);
}

// A failed step used to show nothing but its status: the user could not tell a
// duplicate contact from a rejected approval. Map the stable code the gateway
// stores on the item to a readable reason.
const ITEM_ERROR_KEYS: Readonly<Record<string, string>> = {
  duplicate_contact_email: 'crm.ai_chat.action_plan.error.duplicate_email',
  duplicate_contact_phone: 'crm.ai_chat.action_plan.error.duplicate_phone',
  handler_validation_failed: 'crm.ai_chat.action_plan.error.validation',
  aborted_before_handler: 'crm.ai_chat.action_plan.retry_before_effect',
  scope_recheck_failed: 'crm.ai_chat.action_plan.retry_before_effect',
  handler_failed: 'crm.ai_chat.action_plan.error.handler',
  handler_aborted_unknown_effect: 'crm.ai_chat.action_plan.error.handler',
  dependency_not_satisfied: 'crm.ai_chat.action_plan.error.dependency',
  stale_write: 'crm.ai_chat.action_plan.error.stale',
  compound_source_changed: 'crm.ai_chat.action_plan.error.compound_source_changed',
};

/** Id of a created/updated contact or deal for the «open» button (main #975: item.result; legacy fields fallback). */
function openEntityId(item: CrmActionPlanItem): string {
  if (item.status !== 'succeeded') return '';
  const fromResult =
    item.entity.type === 'contact'
      ? item.result?.contactId
      : item.entity.type === 'deal'
        ? item.result?.dealId
        : undefined;
  if (typeof fromResult === 'string' && fromResult !== '') return fromResult;
  const idField = item.fields.find((f) => f.label === 'id' || f.label === `${item.entity.type}Id`);
  return typeof idField?.value === 'string' ? idField.value : '';
}

function itemErrorKey(error: string | undefined): string | undefined {
  if (error === undefined) return undefined;
  return (
    ITEM_ERROR_KEYS[error] ??
    (error.startsWith('duplicate_contact_') ? 'crm.ai_chat.action_plan.error.duplicate' : undefined)
  );
}

function StatusIcon({ status }: { status: CrmActionPlanItem['status'] }): ReactElement {
  if (status === 'succeeded') return <Check size={14} aria-hidden="true" />;
  if (status === 'rejected' || status === 'cancelled' || status === 'undone') {
    return <CircleSlash2 size={14} aria-hidden="true" />;
  }
  if (status === 'failed' || status === 'stale') return <X size={14} aria-hidden="true" />;
  return <Clock3 size={14} aria-hidden="true" />;
}

/** Tag color for one previewed bulk element status (label-only sample). */
const BULK_SAMPLE_STATUS_TONES: Readonly<Record<string, EntityStatusTone>> = {
  pending: 'warning',
  succeeded: 'success',
  failed: 'error',
  skipped: 'default',
  stale: 'error',
};

type CrmActionPlanBulk = NonNullable<CrmActionPlanItem['bulk']>;

/**
 * PR 3: per-item outcome block of a homogeneous bulk group. Shows the durable
 * coordinator's aggregate (only non-zero counters), the label-only preview
 * sample with per-element statuses, and a safe retry button: re-running the
 * coordinator claims only pending or lease-expired elements, succeeded
 * elements never re-execute. No ids, filters, or raw JSON cross this
 * boundary.
 */
function BulkOutcomeBlock({
  bulk,
  canRetry,
  isBusy,
  onRetry,
}: {
  bulk: CrmActionPlanBulk;
  canRetry: boolean;
  isBusy: boolean;
  onRetry: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const aggregate = bulk.aggregate;
  return (
    <div className="crm-action-plan-bulk space-y-2" data-testid="crm-action-plan-bulk">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-stone-500">
          {t('crm.ai_chat.action_plan.bulk.total', { count: bulk.count })}
        </span>
        <EntityStatusChip tone="success" className="m-0">
          {t('crm.ai_chat.action_plan.bulk.succeeded', { count: aggregate.succeeded })}
        </EntityStatusChip>
        {aggregate.failed > 0 ? (
          <EntityStatusChip tone="error" className="m-0">
            {t('crm.ai_chat.action_plan.bulk.failed', { count: aggregate.failed })}
          </EntityStatusChip>
        ) : null}
        {aggregate.skipped > 0 ? (
          <Tag className="m-0">
            {t('crm.ai_chat.action_plan.bulk.skipped', { count: aggregate.skipped })}
          </Tag>
        ) : null}
        {aggregate.stale > 0 ? (
          <EntityStatusChip tone="error" className="m-0">
            {t('crm.ai_chat.action_plan.bulk.stale', { count: aggregate.stale })}
          </EntityStatusChip>
        ) : null}
        {aggregate.unfinished > 0 ? (
          <EntityStatusChip tone="warning" className="m-0">
            {t('crm.ai_chat.action_plan.bulk.unfinished', { count: aggregate.unfinished })}
          </EntityStatusChip>
        ) : null}
      </div>
      {bulk.sample.length > 0 ? (
        <div className="space-y-1">
          <p className="m-0 text-xs text-stone-500">
            {t('crm.ai_chat.action_plan.bulk.sample_title', { count: bulk.sample.length })}
          </p>
          <ul className="m-0 list-none space-y-1 p-0">
            {bulk.sample.map((sample, index) => (
              <li
                key={`${sample.label}-${index}`}
                className="flex min-w-0 items-center justify-between gap-2 text-xs"
                data-testid={`crm-action-plan-bulk-sample-${index}`}
              >
                <span className="min-w-0 truncate font-medium text-stone-800">{sample.label}</span>
                <EntityStatusChip
                  tone={BULK_SAMPLE_STATUS_TONES[sample.status] ?? 'default'}
                  className="m-0 shrink-0"
                >
                  {t(`crm.ai_chat.action_plan.bulk.item_status.${sample.status}`)}
                </EntityStatusChip>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {canRetry ? (
        <Button size="small" loading={isBusy} onClick={onRetry}>
          {t('crm.ai_chat.action_plan.bulk.retry')}
        </Button>
      ) : null}
    </div>
  );
}

export function CrmActionPlanCard({
  plan,
  onDecideActionPlanItem,
  onRequestActionPlanUndo,
  onRequestActionPlanRetry,
  onOpenEntity,
}: CrmActionPlanCardProps): ReactElement {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const singleItem = plan.items.length === 1;
  const [busyItemId, setBusyItemId] = useState<string | null>(null);

  const decide = async (
    item: CrmActionPlanItem,
    decision: CrmActionPlanDecisionRequest['decision'],
  ): Promise<void> => {
    if (!item.approvalId || !onDecideActionPlanItem) return;
    setBusyItemId(item.id);
    try {
      await onDecideActionPlanItem({
        planId: plan.id,
        itemId: item.id,
        approvalId: item.approvalId,
        decision,
      });
    } catch {
      // Without the toast a failed decision looked exactly like a slow one:
      // the item stayed "pending" and the user kept clicking.
      void message.error(t('crm.ai_chat.action_plan.decide_failed'));
    } finally {
      setBusyItemId(null);
    }
  };

  const retry = async (item: CrmActionPlanItem): Promise<void> => {
    if (!item.retryExecutionId || !onRequestActionPlanRetry) return;
    setBusyItemId(item.id);
    try {
      await onRequestActionPlanRetry({
        planId: plan.id,
        itemId: item.id,
        failedExecutionId: item.retryExecutionId,
      });
    } catch {
      void message.error(t('crm.ai_chat.action_plan.retry_failed'));
    } finally {
      setBusyItemId(null);
    }
  };

  const undo = async (item: CrmActionPlanItem): Promise<void> => {
    if (!onRequestActionPlanUndo) return;
    setBusyItemId(item.id);
    try {
      await onRequestActionPlanUndo({ planId: plan.id, itemId: item.id });
    } catch {
      void message.error(t('crm.ai_chat.action_plan.undo_failed'));
    } finally {
      setBusyItemId(null);
    }
  };

  return (
    <Card
      size="small"
      data-testid={`crm-action-plan-${plan.id}`}
      title={singleItem ? undefined : t('crm.ai_chat.action_plan.title')}
      className="w-full max-w-2xl"
    >
      {busyItemId === 'remaining' || plan.groupExecution?.state === 'executing' ? (
        <div role="status">{t('crm.ai_chat.action_plan.group_executing')}</div>
      ) : null}
      <ol className="m-0 list-none space-y-2 p-0">
        {[...plan.items]
          .sort((left, right) => left.ordinal - right.ordinal)
          .map((item, index) => {
            const visibleFields = item.fields.flatMap((field) => {
              const labelKey = FIELD_KEYS[field.label];
              return labelKey === undefined ||
                (field.label === 'title' && field.value === item.entity.label)
                ? []
                : [{ ...field, labelKey }];
            });
            const groupResult = plan.groupExecution?.results.find(
              (row) => row.ordinal === item.ordinal,
            );
            const displayStatus = groupResult
              ? groupResult.status === 'succeeded'
                ? 'succeeded'
                : groupResult.status === 'failed'
                  ? 'failed'
                  : 'approved'
              : item.status;
            const error = groupResult ? groupResult.error : item.error;
            const showRemaining = Boolean(
              plan.remainingApproval &&
              plan.remainingApproval.count >= 2 &&
              onDecideActionPlanItem &&
              item.id === plan.items.find((row) => row.status === 'pending' && row.approvalId)?.id,
            );
            const isBusy = busyItemId === item.id;
            const canDecide =
              !groupResult &&
              busyItemId !== 'remaining' &&
              item.status === 'pending' &&
              item.approvalId !== undefined &&
              onDecideActionPlanItem !== undefined;
            const canUndo =
              item.status === 'succeeded' &&
              item.canUndo === true &&
              onRequestActionPlanUndo !== undefined;
            // PR 4: a succeeded step whose result was consumed by a successful
            // downstream item can no longer be undone — say so instead of
            // silently hiding the button.
            const undoBlockedByDownstream =
              item.status === 'succeeded' &&
              !canUndo &&
              plan.items.some(
                (child) =>
                  child.dependsOnItemId === item.id &&
                  (child.status === 'succeeded' || child.status === 'undo_pending'),
              );

            return (
              <li
                key={item.id}
                data-testid={`crm-action-plan-item-${item.id}`}
                data-status={item.status}
                className="crm-action-plan-item min-w-0"
                style={{
                  border: singleItem ? undefined : `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusLG,
                  padding: singleItem ? 0 : token.paddingSM,
                  background: token.colorBgContainer,
                }}
              >
                <div className="flex min-w-0 items-start gap-3">
                  {!singleItem ? (
                    <span
                      data-testid={`crm-action-plan-ordinal-${item.id}`}
                      style={{
                        color: token.colorTextSecondary,
                        background: token.colorFillAlter,
                        borderRadius: token.borderRadius,
                        padding: `0 ${token.paddingXS}px`,
                      }}
                    >
                      {index + 1}
                    </span>
                  ) : null}
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="m-0 text-xs" style={{ color: token.colorTextSecondary }}>
                          {t(
                            ACTION_KEYS[item.action] ??
                              'crm.ai_chat.action_plan.action.unspecified',
                          )}
                        </p>
                        <h3
                          className="m-0 wrap-anywhere text-sm font-semibold"
                          style={{ color: token.colorText }}
                        >
                          {item.entity.label}
                        </h3>
                      </div>
                      <div className="min-w-0 space-y-1">
                        <EntityStatusChip
                          tone={STATUS_TONES[displayStatus]}
                          icon={<StatusIcon status={displayStatus} />}
                          className="m-0 inline-flex max-w-full items-center gap-1 whitespace-normal"
                        >
                          {t(
                            groupResult
                              ? 'crm.ai_chat.action_plan.group_' + groupResult.status
                              : item.error === 'batch_approval_expired'
                                ? 'crm.approvals.expired'
                                : item.status === 'cancelled' && item.error === 'batch_item_removed'
                                  ? 'crm.chat.compound.status.cancelled'
                                  : `crm.ai_chat.action_plan.status.${item.status}`,
                          )}
                        </EntityStatusChip>
                        {groupResult?.status === 'failed' ? (
                          <p className="m-0 text-xs text-red-700 dark:text-red-400">
                            {t(itemErrorKey(error) ?? 'crm.ai_chat.action_plan.error.handler')}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {visibleFields.length > 0 ? (
                      <dl
                        className="crm-action-plan-fields m-0 text-xs"
                        style={{
                          gridTemplateColumns: 'minmax(0, 30%) minmax(0, 1fr)',
                          columnGap: token.marginSM,
                          rowGap: token.marginXS,
                        }}
                      >
                        {visibleFields.map((field, fieldIndex) => (
                          <div
                            key={`${field.labelKey}-${fieldIndex}`}
                            className="crm-action-plan-field"
                            style={{ display: 'contents' }}
                          >
                            <dt
                              className="min-w-0 wrap-anywhere"
                              style={{ color: token.colorTextSecondary }}
                            >
                              {t(field.labelKey)}:
                            </dt>
                            <dd
                              className="m-0 min-w-0 wrap-anywhere font-medium"
                              style={{ color: token.colorText }}
                            >
                              {field.label === 'priority' &&
                              ['low', 'medium', 'high'].includes(String(field.value))
                                ? t(`crm.chat.action_plan.priority.${String(field.value)}`)
                                : formatFieldValue(
                                    field.label,
                                    field.value,
                                    t('crm.ai_chat.action_plan.empty_value'),
                                  )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}

                    {item.bulk !== undefined ? (
                      <BulkOutcomeBlock
                        bulk={item.bulk}
                        canRetry={
                          item.status === 'succeeded' &&
                          item.bulk.aggregate.unfinished > 0 &&
                          item.approvalId !== undefined &&
                          onDecideActionPlanItem !== undefined
                        }
                        isBusy={isBusy}
                        onRetry={() => void decide(item, 'approve')}
                      />
                    ) : null}

                    {!groupResult && itemErrorKey(error) !== undefined ? (
                      <p className="m-0 text-xs text-red-700 dark:text-red-400">
                        {t(itemErrorKey(error) ?? 'crm.ai_chat.action_plan.error.handler')}
                      </p>
                    ) : null}
                    {!groupResult && item.status === 'blocked' ? (
                      <p className="m-0 text-xs text-stone-500">
                        {t('crm.ai_chat.action_plan.blocked_dependency')}
                      </p>
                    ) : null}
                    {item.status === 'stale' ? (
                      <p className="m-0 text-xs text-red-700 dark:text-red-400">
                        {t('crm.ai_chat.action_plan.stale_details')}
                      </p>
                    ) : null}

                    {showRemaining ||
                    canDecide ||
                    canUndo ||
                    undoBlockedByDownstream ||
                    (item.status === 'failed' &&
                      item.retryExecutionId &&
                      onRequestActionPlanRetry) ||
                    (item.status === 'succeeded' &&
                      onOpenEntity &&
                      (item.entity.type === 'contact' || item.entity.type === 'deal') &&
                      openEntityId(item) !== '') ? (
                      <footer
                        data-testid={`crm-action-plan-footer-${item.id}`}
                        style={{
                          borderTop: `1px solid ${token.colorBorderSecondary}`,
                          paddingTop: token.paddingSM,
                          marginTop: token.marginSM,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: token.marginXS,
                        }}
                      >
                        {item.status === 'failed' &&
                        item.retryExecutionId &&
                        onRequestActionPlanRetry ? (
                          <Button
                            size="small"
                            icon={<RotateCcw size={14} aria-hidden="true" />}
                            loading={isBusy}
                            disabled={isBusy}
                            onClick={() => void retry(item)}
                          >
                            {t('crm.ai_chat.action_plan.retry')}
                          </Button>
                        ) : null}

                        {showRemaining ? (
                          <div data-testid="crm-action-plan-remaining">
                            <Button
                              type="primary"
                              loading={busyItemId === 'remaining'}
                              disabled={busyItemId !== null}
                              onClick={async () => {
                                const item = plan.items.find(
                                  (row) => row.approvalId && row.status === 'pending',
                                );
                                if (!item?.approvalId) return;
                                setBusyItemId('remaining');
                                try {
                                  await onDecideActionPlanItem?.({
                                    planId: plan.id,
                                    itemId: item.id,
                                    approvalId: item.approvalId,
                                    decision: 'approve',
                                    remainingToken: plan.remainingApproval!.token,
                                  });
                                } catch (error) {
                                  void message.error(
                                    error instanceof ApiError && error.status === 409
                                      ? error.message
                                      : t('crm.ai_chat.action_plan.decide_failed'),
                                  );
                                } finally {
                                  setBusyItemId(null);
                                }
                              }}
                            >
                              {t('crm.ai_chat.action_plan.approve_remaining', {
                                count: plan.remainingApproval!.count,
                              })}
                            </Button>
                          </div>
                        ) : null}

                        {canDecide ? (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="primary"
                              size="small"
                              loading={isBusy}
                              onClick={() => void decide(item, 'approve')}
                            >
                              {t('crm.ai_chat.action_plan.approve')}
                            </Button>
                            <Button
                              size="small"
                              disabled={isBusy}
                              onClick={() => void decide(item, 'reject')}
                            >
                              {t('crm.ai_chat.action_plan.reject')}
                            </Button>
                          </div>
                        ) : canUndo ? (
                          <Button
                            size="small"
                            icon={<RotateCcw size={14} aria-hidden="true" />}
                            loading={isBusy}
                            onClick={() => void undo(item)}
                          >
                            {t('crm.ai_chat.action_plan.undo')}
                          </Button>
                        ) : undoBlockedByDownstream ? (
                          <p
                            className="m-0 text-xs text-stone-500"
                            data-testid={`crm-action-plan-undo-blocked-${item.id}`}
                          >
                            {t('crm.ai_chat.action_plan.undo_used_downstream')}
                          </p>
                        ) : null}

                        {(() => {
                          const entityId = openEntityId(item);
                          return item.status === 'succeeded' &&
                            onOpenEntity &&
                            entityId &&
                            (item.entity.type === 'contact' || item.entity.type === 'deal') ? (
                            <div className="pt-1">
                              <Button
                                size="small"
                                onClick={() =>
                                  onOpenEntity(
                                    item.entity.type as 'contact' | 'deal',
                                    entityId,
                                    item.result?.title ?? item.entity.label,
                                  )
                                }
                                className="inline-flex items-center gap-1 text-xs"
                              >
                                {item.entity.type === 'contact'
                                  ? t('crm.ai_chat.action_plan.view_contact')
                                  : t('crm.ai_chat.action_plan.view_deal')}
                              </Button>
                            </div>
                          ) : null;
                        })()}
                      </footer>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
      </ol>
    </Card>
  );
}
